import { useNavigate } from "@tanstack/react-router";
import { useCallback, useRef, useState } from "react";
import { cn } from "../lib/cn.js";
import { normaliseSymbol } from "../lib/normaliseSymbol.js";
import { useHotkey } from "../lib/useHotkey.js";
import { type InstrumentMatch, useInstrumentSearch } from "../lib/useInstrumentSearch.js";

import { SymbolSearchResults } from "./SymbolSearchResults.js";
import { PopoverAnchor, PopoverRoot } from "./ui/Popover.js";

/**
 * The way into the symbol workspace, for a reader who may not know the code.
 *
 * It replaced an exact-ticker form. Someone who knew "Bajaj Finance" and not
 * `BAJFINANCE` had no way in, and a typo rendered a page reading "Nothing to
 * show" for a name that cannot exist. Now the list is the answer and typing
 * blind is the fallback rather than the only route.
 *
 * Cmd/Ctrl+K focuses it from anywhere, because this is the one control every
 * page carries and reaching for the mouse to change symbol is the most
 * repeated action in the app.
 */

/** Rows are keyboard-reachable, so the highlighted one needs an index. */
function clampIndex(index: number, length: number): number {
  if (length === 0) return -1;
  if (index < 0) return length - 1;
  if (index >= length) return 0;
  return index;
}

export function SymbolSearch(): React.JSX.Element {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState("");
  const [active, setActive] = useState(-1);

  const { data, isFetching } = useInstrumentSearch(value);
  const matches: InstrumentMatch[] = data?.matches ?? [];
  const open = value.trim().length > 0;

  useHotkey(
    "k",
    useCallback(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, []),
  );

  const go = useCallback(
    (symbol: string) => {
      setValue("");
      setActive(-1);
      inputRef.current?.blur();
      void navigate({ to: "/symbol/$symbol", params: { symbol } });
    },
    [navigate],
  );

  /** Enter takes the highlighted row, or what was typed if none is. */
  const submit = () => {
    const picked = matches[active];
    if (picked) return go(picked.symbol);
    const typed = normaliseSymbol(value);
    if (typed) go(typed);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setActive((current) =>
        clampIndex(current + (event.key === "ArrowDown" ? 1 : -1), matches.length),
      );
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      submit();
      return;
    }
    if (event.key === "Escape") {
      setValue("");
      setActive(-1);
    }
  };

  return (
    <PopoverRoot open={open && matches.length > 0}>
      <PopoverAnchor asChild>
        <div className="flex items-center">
          <label className="sr-only" htmlFor="symbol-search">
            Go to symbol
          </label>
          <input
            ref={inputRef}
            id="symbol-search"
            type="search"
            role="combobox"
            aria-expanded={open && matches.length > 0}
            aria-controls="symbol-search-list"
            aria-autocomplete="list"
            autoComplete="off"
            value={value}
            placeholder="Symbol or name"
            onChange={(event) => {
              setValue(event.target.value);
              // The old highlight belongs to the old list.
              setActive(-1);
            }}
            onKeyDown={onKeyDown}
            className={cn(
              "w-44 border border-border bg-surface px-2 py-1 text-sm",
              "text-text placeholder:text-text-muted",
            )}
          />
          {/* Read out rather than hidden: the shortcut is the fastest way to
              this control and a screen-reader user wants it more, not less. */}
          <kbd className="ml-2 hidden border border-border px-1 text-xs text-text-muted sm:inline">
            ⌘K
          </kbd>
        </div>
      </PopoverAnchor>

      <SymbolSearchResults
        matches={matches}
        activeIndex={active}
        onHover={setActive}
        onPick={go}
        searching={isFetching}
      />
    </PopoverRoot>
  );
}
